import { BenchmarkRun, WithheldMetric } from '../../services/benchmarkService';

/**
 * What a run refused to compute, and what it computed with a caveat, indexed
 * by the metric family a panel asks about.
 *
 * The run records these in `withheld_metrics`. An entry with `qualifies` adds
 * a caveat to the families it names. Any other entry withholds its own family,
 * and every family in `suppresses` with it — the same fold the scorer applies,
 * so the dashboard hides exactly what the run declined to measure.
 */
export interface Qualification {
    /** The entry that raised it, e.g. `layer2:as_of`. */
    source: string;
    /** Plain-language copy where there is some, otherwise the run's own reason. */
    summary: string;
    reason: string;
}

export interface WithheldIndex {
    /** Family → the reasons it was withheld. */
    suppressed: Map<string, string[]>;
    /** Family → the caveats it carries. */
    qualified: Map<string, Qualification[]>;
    /** Every entry's own family, whether it withholds or qualifies. */
    present: Set<string>;
}

/**
 * Copy for the qualifications a reader is most likely to meet on a supplied
 * sample. Everything else shows the run's reason verbatim.
 */
export const QUALIFICATION_COPY: Record<string, string> = {
    'kyc:post_alert': "Some KYC was dated after the alert and may reflect the investigation's outcome.",
    'layer2:as_of': 'KYC may include changes made after the alert.',
    'ranking:frame': 'Measured only over alerts your monitoring raised, so it cannot see cases it never alerted on.',
    context_semantics: 'The context features behind this run are a synthetic fixture.'
};

function add<T>(map: Map<string, T[]>, key: string, value: T) {
    const list = map.get(key);
    if (list) list.push(value);
    else map.set(key, [value]);
}

export function buildWithheldIndex(entries: WithheldMetric[] | null | undefined): WithheldIndex {
    const index: WithheldIndex = { suppressed: new Map(), qualified: new Map(), present: new Set() };
    for (const entry of entries ?? []) {
        if (!entry?.metric_family) continue;
        const family = entry.metric_family;
        const reason = entry.reason ?? '';
        index.present.add(family);

        const qualifies = entry.qualifies ?? [];
        if (qualifies.length) {
            const qualification: Qualification = {
                source: family,
                summary: QUALIFICATION_COPY[family] ?? reason,
                reason
            };
            for (const target of qualifies) add(index.qualified, target, qualification);
        } else {
            add(index.suppressed, family, reason);
        }
        for (const target of entry.suppresses ?? []) add(index.suppressed, target, reason);
    }
    return index;
}

/** Why any of these families was withheld; empty when none was. */
export function suppressionReasons(index: WithheldIndex, families: readonly string[]): string[] {
    return families.flatMap((family) => index.suppressed.get(family) ?? []);
}

export function isSuppressed(index: WithheldIndex, families: readonly string[]): boolean {
    return families.some((family) => index.suppressed.has(family));
}

/** The caveats on any of these families, each listed once. */
export function qualificationsFor(index: WithheldIndex, families: readonly string[]): Qualification[] {
    const seen = new Set<string>();
    const result: Qualification[] = [];
    for (const family of families) {
        for (const q of index.qualified.get(family) ?? []) {
            if (seen.has(q.source)) continue;
            seen.add(q.source);
            result.push(q);
        }
    }
    return result;
}

/**
 * Whether every sampled customer's Layer 2 evidence came from supplied KYC
 * dated before their first alert.
 *
 * The run says so by omission: `layer2:as_of` qualifies Layer 2 for look-ahead
 * unless that holds. Only claimed for a supplied run that actually read KYC —
 * on a stock run the entry's absence means Layer 2 was not in play at all.
 */
export function isKycPointInTime(run: BenchmarkRun | null | undefined, index: WithheldIndex): boolean {
    if (!run || run.source?.dataSource !== 'user_supplied') return false;
    const kyc = run.source.kyc;
    if (!kyc || !kyc.suppliedCustomers) return false;
    return !index.present.has('layer2:as_of') && !index.present.has('kyc:post_alert');
}

/**
 * Which metric families each dashboard panel draws on.
 *
 * `ranking` is how well the agent's order separates outcomes; `head_to_head`
 * compares it with the incumbent's; `concordance` compares tiers with the
 * incumbent's own; the Layer families describe why the agent ranked as it did.
 */
export const PANEL_FAMILIES = {
    rankingQuality: ['ranking'],
    headToHead: ['head_to_head'],
    concordance: ['concordance'],
    scenarios: ['ranking'],
    stability: ['ranking'],
    riskFeatures: ['feature_discrimination'],
    layerContributions: ['layer_semantics']
} as const;

/** For a panel rendered without a run to hand: nothing withheld. */
export const NO_WITHHELD: WithheldIndex = buildWithheldIndex([]);
